/**
 * Christmas table settings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_christmas_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const christmasTableSettingsPack: ToilePack = {
    slug: 'christmas-table-settings',
    category: 'season',
    name: 'Christmas table settings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'classic', label: 'Classic', subject: 'a table with red napkins and candles'},
        {key: 'scandi', label: 'Scandinavian', subject: 'a table with white linen and greenery'},
        {key: 'gold', label: 'Gold', subject: 'a table with gold chargers and crystal'},
    ],
    styles: [
        {key: 'overhead', label: 'From above', style: 'a tablescape seen from directly above, soft daylight, an even balance with gentle shadows under the plates', palette: 'the theme colors on the tablecloth', framing: 'one place setting and the centerpiece filling the frame'},
        {key: 'candlelit', label: 'Candlelit', style: 'the table at dinner time lit by candle glow and warm light from camera-right, a dark-to-light balance with a dim room behind', palette: 'warm gold glow on the theme colors', framing: 'low three-quarter view down the length of the table'},
        {key: 'daylight', label: 'Daylight', style: 'laid in a bright dining room in soft window daylight from camera-left, an airy bright balance with soft shadow', palette: 'the theme colors with pale walls and wood chairs', framing: 'eye-level wide shot of the whole table and chairs'},
    ],
    directions: ['kids table', "new year's eve", 'rustic wood'],
    directionHint: 'try: kids table',
    seo: {
        title: 'Christmas Table Settings: Classic, Scandinavian, Gold',
        metaDescription: 'Nine photos of a Christmas table in three themes, classic red, Scandinavian and gold, seen from above, by candlelight and by day.',
        h1: 'Christmas table settings: classic, Scandinavian, gold',
        tagline: 'Three table themes, each shown from above, by candlelight and in daylight.',
        intro: 'Three Christmas table themes: classic with red napkins and candles, Scandinavian with white linen and greenery, and gold with gold chargers and crystal. Each table is shown from above, at dinner time by candle glow, and in a bright dining room by day. Use them to plan a table before guests arrive. Type a twist, such as kids table or rustic wood, and the table is asked to match it.',
        useCases: [
            'planning the table before guests arrive',
            'a reference for a holiday dinner at home',
            "a plan for a kids' table",
            'a sketch to show whoever sets the table',
        ],
        faq: [
            {q: 'What is on each table?', a: 'The classic table has red napkins and candles. The Scandinavian table has white linen and greenery. The gold table has gold chargers and crystal.'},
            {q: 'What are the three views?', a: 'From above shows one place setting and the centerpiece. Candlelit shows the table at dinner time from a low angle. Daylight shows the whole table and chairs in a bright room.'},
            {q: 'Can I ask for a different table?', a: "Yes. Type a twist, such as new year's eve or rustic wood, and the page asks for a table that follows it."},
        ],
    },
}
